import React, { Suspense, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { perSessionModalKey } from 'constants/localStorageKeys';
import { Text } from 'shared/components/Text/Text';
import { Modal } from 'shared/components/Modal/Modal';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { OnboardingStep } from 'modules/types/util';
import {
  GraphicContainer,
  ListWrapper,
  PreInstallationModalBody,
  Title,
} from './MilestoneCompleteModal.styles';
import { HouseGraphic } from './HouseGraphic';

const CheckmarkGraphic = React.lazy(() => import('shared/components/icons/CheckmarkGraphic'));

type Props = {
  modalKey?: string;
  showModal: boolean;
  onboardingStep: OnboardingStep;
  onClickContinue: () => void;
  onClose: () => void;
};

export function MilestoneCompleteModal({
  modalKey,
  showModal,
  onboardingStep,
  onClickContinue,
  onClose,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.milestoneCompleteModal' });
  const { t: tBase } = useTranslation();

  let title = t('preInstallation.title');
  let bodyKey = 'preInstallation.body';
  let graphic = <CheckmarkGraphic />;
  if (onboardingStep === OnboardingStep.SYSTEM_INSTALLATION) {
    title = t('installation.title');
    bodyKey = 'installation.body';
  }
  if (onboardingStep === OnboardingStep.SYSTEM_ACTIVATION) {
    title = t('systemActivation.title');
    bodyKey = 'systemActivation.body';
    graphic = <HouseGraphic />;
  }

  const bodyList: string[] = t(bodyKey, {
    returnObjects: true,
  }) as string[];

  useEffect(() => {
    if (!showModal) {
      return;
    }

    const perSessionModalsRaw = localStorage.getItem(perSessionModalKey);

    if (perSessionModalsRaw != null && modalKey) {
      const modalsObj = JSON.parse(perSessionModalsRaw);
      const savedModalVisible =
        modalsObj[modalKey] === undefined ? (modalsObj[modalKey] = true) : modalsObj[modalKey];
      localStorage.setItem(perSessionModalKey, JSON.stringify(modalsObj));
      setIsOpen(savedModalVisible);
    } else if (modalKey) {
      localStorage.setItem(perSessionModalKey, JSON.stringify({ [modalKey]: true }));
      setIsOpen(true);
    }
  }, [modalKey, showModal]);

  const handleClose = () => {
    const perSessionModalsRaw = localStorage.getItem(perSessionModalKey);
    if (perSessionModalsRaw != null && modalKey) {
      const modalsObj = JSON.parse(perSessionModalsRaw);
      modalsObj[modalKey] = false;
      localStorage.setItem(perSessionModalKey, JSON.stringify(modalsObj));
    }
    onClose();
    setIsOpen(false);
  };

  const handleContinue = () => {
    onClickContinue();
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      contentLabel={title}
      styleVariant="tertiary"
      isFullHeight
      fitContent
      onRequestClose={handleClose}
      ctaButtons={{
        primary: {
          label: tBase('cta.continue'),
          onClick: handleContinue,
        },
      }}
    >
      <PreInstallationModalBody>
        <GraphicContainer>
          <Suspense fallback={<LoadingSpinner />}>{graphic}</Suspense>
        </GraphicContainer>
        <Title as="h2">{title}</Title>
        <ListWrapper>
          <Trans components={{ strong: <b /> }}>
            {Array.isArray(bodyList) &&
              bodyList.map((item, idx) => {
                const key = `milestone-complete-${idx}`;
                return (
                  <Text key={key} as="p">
                    {item}
                  </Text>
                );
              })}
          </Trans>
        </ListWrapper>
      </PreInstallationModalBody>
    </Modal>
  );
}

export default MilestoneCompleteModal;
